import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import type {
  CellClassParams,
  ColDef,
  GridApi,
  ICellRendererParams,
  IRowNode,
  ValueFormatterParams,
  ValueSetterParams,
} from 'ag-grid-community'
import { Form, type FormInstance, message } from 'antd'
import { useRef, useState } from 'react'
import type { MutableRefObject } from 'react'
import type { MileageBand } from '../types'
import { updateContiguousToMiles } from '../helpers'

export interface UseEditableMileageBandsGridProps {
  rateType: string
  dirtyGridApiRef: MutableRefObject<unknown>
  editingCopyRows: MileageBand[]
  setEditingCopyRows: React.Dispatch<React.SetStateAction<MileageBand[]>>
  onHasDuplicatesChange: (hasDuplicates: boolean) => void
  onHasInvalidMileValuesChange: (hasInvalid: boolean) => void
}

interface UseEditableMileageBandsGridResult {
  gridApiRef: MutableRefObject<GridApi<MileageBand> | null>
  popoverForm: FormInstance<MileageBand>
  duplicateMiles: Set<number>
  lastRowEditPopoverVisible: boolean
  editingNodeId: string | null

  handleAddMileageBand: () => void
  handleDeleteMileageBand: (key: string) => void
  handleSaveLastToMile: () => Promise<void>
  handleCancelLastToMile: () => void
  handleGridReady: (params: { api: GridApi<MileageBand> }) => void
  handleRowDataUpdated: (params: {
    api: GridApi<MileageBand>
  }) => Promise<void>
  getColumnDefs: () => ColDef[]
  conditionalCellStyle: (params: CellClassParams<MileageBand>) => string | null
  setLastRowEditPopoverVisible: (visible: boolean) => void
  setEditingNodeId: (nodeId: string | null) => void
}
export function useEditableMileageBandsGrid({
  rateType,
  dirtyGridApiRef,
  editingCopyRows,
  setEditingCopyRows,
  onHasDuplicatesChange,
  onHasInvalidMileValuesChange,
}: UseEditableMileageBandsGridProps): UseEditableMileageBandsGridResult {
  const formattedRateType = rateType ? ` (${rateType})` : ''
  const gridApiRef = useRef<GridApi<MileageBand>>(null)

  const [duplicateMiles, setDuplicateMiles] = useState<Set<number>>(new Set())
  const [lastRowEditPopoverVisible, setLastRowEditPopoverVisible] = useState(false)
  const [editingNodeId, setEditingNodeId] = useState<string | null>(null)
  const [popoverForm] = Form.useForm()

  // Function to find duplicate from_mile values
  const findDuplicateFromMiles = (rows: MileageBand[]): Set<number> => {
    const seenMiles = new Set<number>()
    const duplicates = new Set<number>()
    for (const row of rows) {
      if (row.from_mile !== undefined && row.from_mile !== null) {
        if (seenMiles.has(row.from_mile)) {
          duplicates.add(row.from_mile)
        } else {
          seenMiles.add(row.from_mile)
        }
      }
    }
    return duplicates
  }

  const handleAddMileageBand = () => {
    // Create a new blank row with a temporary key
    const tempKey = `new-${Date.now()}` // Unique temporary key
    const newRow: MileageBand = {
      key: tempKey,
      from_mile: undefined,
      to_mile: undefined,
      rate: 0,
    }

    // Add the new row to the top using grid API
    if (gridApiRef.current) {
      // Use applyTransaction for adding rows
      gridApiRef.current.applyTransaction({ add: [newRow], addIndex: 0 })

      // Update the state as well, adding to the beginning
      setEditingCopyRows((prevRows) => [newRow, ...prevRows])

      // Start editing the 'from_mile' cell of the newly added row (index 0)
      setTimeout(() => {
        gridApiRef.current?.ensureIndexVisible(0)
        gridApiRef.current?.startEditingCell({
          rowIndex: 0,
          colKey: 'from_mile',
        })
      }, 0)
    } else {
      console.warn('Grid API not available in handleAddMileageBand')
      // Fallback: Update state anyway if grid is not ready
      setEditingCopyRows((prevRows) => [newRow, ...prevRows])
    }
  }

  const handleDeleteMileageBand = (key: string) => {
    if (!gridApiRef.current) {
      console.error('Grid API not available for delete')
      setEditingCopyRows((currentRows) => currentRows.filter((band) => band.key !== key))
      return
    }

    // Find the row data to remove
    const rowDataToRemove = editingCopyRows.find((band) => band.key === key)

    if (rowDataToRemove) {
      // Use grid transaction to remove the row
      gridApiRef.current.applyTransaction({
        remove: [rowDataToRemove],
      })
    } else {
      console.warn('Row to delete not found with key:', key)
    }
  }

  const handleSaveLastToMile = async () => {
    try {
      // Validate form first
      const values = await popoverForm.validateFields()
      const newToMileValue = values.to_mile

      if (editingNodeId && gridApiRef.current) {
        const node = gridApiRef.current.getRowNode(editingNodeId)
        if (node?.data) {
          // Validation is now handled by Form rules, but keep the check for >= from_mile
          if (node.data.from_mile !== undefined && newToMileValue < node.data.from_mile) {
            message.error("'To Mile' must be greater than or equal to 'From Mile'.")
            return
          }
          // Use applyTransactionAsync instead of setDataValue
          const updateData = { ...node.data, to_mile: newToMileValue }
          await gridApiRef.current.applyTransactionAsync({
            update: [updateData],
          })
        }
      }
      setLastRowEditPopoverVisible(false)
      setEditingNodeId(null)
    } catch (errorInfo) {
      return
    }
  }

  const handleCancelLastToMile = () => {
    setLastRowEditPopoverVisible(false)
    setEditingNodeId(null)
  }

  // Define the conditional cell style function
  const conditionalCellStyle = (params: CellClassParams<MileageBand>) => {
    if (params.data) {
      // Only apply style for new rows
      if (typeof params.data.key === 'string' && params.data.key.startsWith('new-')) {
        return { background: 'var(--theme-warning-dim)' } // New row style (yellow background)
      }
    }
    return null // No special style
  }

  // Column definitions
  const getColumnDefs = (): ColDef[] => [
    {
      headerName: 'Miles',
      field: 'from_mile',
      sortable: true,
      sort: 'asc' as const,
      editable: true,
      minWidth: 180,
      filter: 'agNumberColumnFilter',
      filterParams: {
        defaultOption: 'equals',
        allowedCharPattern: '\\d\\-\\.',
      },
      cellEditor: 'agNumberCellEditor',
      cellEditorParams: {
        min: 0,
      },
      valueSetter: (params: ValueSetterParams<MileageBand>): boolean => {
        const newValue = params.newValue
        // Basic validation (integer, non-negative)
        if (
          newValue === null ||
          newValue === '' ||
          Number.isNaN(Number(newValue)) ||
          !Number.isInteger(Number(newValue)) ||
          Number(newValue) < 0
        ) {
          message.error('Miles must be a non-negative integer.')
          return false // Invalid input type
        }

        const numericNewValue = Number(newValue)

        // If valid, set the value
        params.data.from_mile = numericNewValue
        // Automatically set to_mile = from_mile + 5
        params.data.to_mile = numericNewValue + 5
        return true
      },
      cellRenderer: (params) => {
        // We'll just return true for the JSX that will be handled in the component
        return true
      },
      cellStyle: conditionalCellStyle,
    },
    {
      headerName: `Rate${formattedRateType}`,
      field: 'rate',
      editable: true,
      minWidth: 120,
      filter: 'agNumberColumnFilter',
      filterParams: {
        defaultOption: 'equals',
        allowedCharPattern: '\\d\\-\\.',
      },
      valueFormatter: (params: ValueFormatterParams) => `$${Number(params.value).toFixed(5)}`,
      valueParser: (params) => {
        // Handle both string and number inputs
        const value = typeof params.newValue === 'string' ? params.newValue.replace(/[$,]/g, '') : params.newValue
        return Number(value)
      },
      cellEditor: 'agNumberCellEditor',
      cellEditorParams: {
        min: 0,
        precision: 5,
        defaultValue: 0,
      },
      cellStyle: conditionalCellStyle,
    },
    {
      headerName: '',
      field: 'actions',
      maxWidth: 80,
      editable: false,
      cellStyle: (params: CellClassParams<MileageBand>) => ({
        ...conditionalCellStyle(params),
        padding: 0,
      }),
      cellRenderer: (params: ICellRendererParams<MileageBand>) => {
        // We'll return true and handle the rendering in the component
        return true
      },
    },
  ]

  // Grid event handlers
  const handleGridReady = (params: { api: GridApi<MileageBand> }) => {
    // @ts-ignore
    gridApiRef.current = params.api
    dirtyGridApiRef.current = params.api
  }

  const handleRowDataUpdated = async (params: {
    api: GridApi<MileageBand>
  }) => {
    // Call the function to update to_mile values
    await updateContiguousToMiles(params.api)

    // Recalculate duplicates and check for invalid values
    const currentGridData: MileageBand[] = []
    params.api.forEachNode((node: IRowNode<MileageBand>) => {
      if (node.data?.key) {
        currentGridData.push(node.data)
      }
    })

    const duplicates = findDuplicateFromMiles(currentGridData)
    const hasInvalidValues = currentGridData.some((row) => row.from_mile == null || row.to_mile == null)

    setDuplicateMiles(duplicates)
    onHasDuplicatesChange(duplicates.size > 0)
    onHasInvalidMileValuesChange(hasInvalidValues)

    // Force cell refresh to apply styles/icons immediately
    params.api.refreshCells({
      columns: ['from_mile', 'rate', 'actions'],
      force: true,
    })

    // Synchronize React state with the final grid data
    if (
      editingCopyRows.length !== currentGridData.length ||
      JSON.stringify(editingCopyRows) !== JSON.stringify(currentGridData)
    ) {
      setEditingCopyRows(currentGridData)
    }
  }

  return {
    // State refs
    gridApiRef,
    popoverForm,

    // State values
    duplicateMiles,
    lastRowEditPopoverVisible,
    editingNodeId,

    // Event handlers
    handleAddMileageBand,
    handleDeleteMileageBand,
    handleSaveLastToMile,
    handleCancelLastToMile,
    handleGridReady,
    handleRowDataUpdated,

    // Grid configuration
    getColumnDefs,
    conditionalCellStyle,

    // Setters
    setLastRowEditPopoverVisible,
    setEditingNodeId,
  }
}
